import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { flattenObjectProperties } from 'src/app/@utils/flatten-object-properties';

import { AsyncPipe, Location, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { GtmService } from '@core/services/gtm/gtm.service';
import { MetaService } from '@core/services/meta.service';
import { OpenGraphService } from '@core/services/open-graph.service';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { RelewiseTypes } from '@core/services/relewise/relewise.config';
import { SchemaService } from '@core/services/schema.service';
import { SettingsService } from '@core/services/settings.service';
import { ProductDetailService } from '@features/product-detail/product-detail.service';
import {
    ProductDetailViewModel,
    ProductSearchViewModelIEnumerableRecommendationResponse as relewiseResponse,
    ProductSplashViewModel
} from '@models/api-search.models';
import { Schema } from '@models/structured-data.models';
import { ProductDetailPage, UmbracoImage } from '@models/umbraco.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { formatString } from '@shared/format-string/format-string';
import { ShowMoreComponent } from '@shared/show-more/show-more.component';
import { ProductEncodeSplashesViewModel } from '@models/api.models';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, skip } from 'rxjs/operators';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { RelewiseSliderComponent } from '@features/slider/relewise-slider/relewise-slider.component';
import { UspComponent } from '@shared/usp/usp.component';
import { ImageComponent } from '@shared/image/image/image.component';
import { ProductInformationComponent } from '@features/product-detail/product-information/product-information.component';
import { ProductDetailsComponent } from '@features/product-detail/product-details/product-details.component';
import { ProductSliderComponent } from '@features/product-detail/product-slider/product-slider.component';
import { StructuredDataComponent } from '@shared/seo/structured-data/structured-data.component';
import { ProductComplementaryComponent } from '@features/product-detail/product-complementary/product-complementary.component';

@UntilDestroy()
@Component({
    selector: 'app-product-overview',
    templateUrl: './product-overview.component.html',
    styleUrls: ['./product-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        StructuredDataComponent,
        ProductSliderComponent,
        ProductDetailsComponent,
        ProductInformationComponent,
        NgFor,
        ImageComponent,
        UspComponent,
        RelewiseSliderComponent,
        AsyncPipe,
        TranslateSafePipe,
        ProductComplementaryComponent
    ]
})
export class ProductOverviewComponent implements OnInit {
    public relewiseResponse: Observable<relewiseResponse | null> = new BehaviorSubject(null);
    public relewiseTypes = RelewiseTypes;
    public structuredDataProduct: Schema;

    public splashesBs = new BehaviorSubject<UmbracoImage[]>([]);
    public splashes$ = this.splashesBs.asObservable();

    @ViewChild('showMore')
    public readonly showMoreComponent: ShowMoreComponent;

    @Input()
    public readonly data?: ProductDetailPage;

    constructor(
        private readonly location: Location,
        private readonly router: Router,
        private readonly relewiseTrackingService: RelewiseTrackingService,
        private readonly gtmService: GtmService,
        private readonly schemaService: SchemaService,
        private readonly settingsService: SettingsService,
        private readonly metaService: MetaService,
        private readonly openGraphService: OpenGraphService,
        private readonly activatedRoute: ActivatedRoute,
        public readonly productDetailService: ProductDetailService
    ) {}

    ngOnInit(): void {
        if (!this.data?.product) {
            return;
        }

        this.productDetailService.resetProduct();
        this.productDetailService.setProduct(this.data.product);

        combineLatest([this.productDetailService.productDetail$, this.settingsService.settings$])
            .pipe(untilDestroyed(this))
            .subscribe(([productResponse, settings]) => {
                const product = productResponse as ProductDetailViewModel;

                if (!product) {
                    return;
                }

                this.relewiseTrackingService.trackProduct(product.variantNo);
                this.gtmService.trackingEvents.trackProductDetailImpression(product);

                this.structuredDataProduct = this.schemaService.getProductSchema(
                    product,
                    settings.ecommerceSettings?.currencyCode
                );

                this.setSplashes(product.activeSplashes, settings.productSettings?.encodeSplashes);
                this.setMeta(product);
                this.setOpenGraph(product);
            });

        // Skip initial load, but hook into stream listening for variant changes
        this.productDetailService.productDetail$
            .pipe(
                skip(1),
                filter((product): product is ProductDetailViewModel => !!product),
                untilDestroyed(this)
            )
            .subscribe(product => {
                // on variant changes, we update the URL, keeping any queryparams
                const serializedUrl = this.router.serializeUrl(
                    this.router.createUrlTree([product.productUrl], {
                        queryParams: this.activatedRoute.snapshot.queryParams
                    })
                );
                this.location.replaceState(serializedUrl);
            });
    }

    public trackBySrc(_: number, image: UmbracoImage): string {
        return image.src;
    }

    private setSplashes(activeSplashes?: ProductSplashViewModel[], encodeSplashes?: ProductEncodeSplashesViewModel[]) {
        if (!activeSplashes?.length || !encodeSplashes?.length) {
            return;
        }

        // Run through encode splashes instead of active splashes to make sure we get the right prioritized order
        const splashes = encodeSplashes.reduce((prev: UmbracoImage[], { encodeId, graphic }): UmbracoImage[] => {
            const hasSplash = activeSplashes.some(({ id }) => encodeId === id);
            return hasSplash && graphic ? [...prev, graphic] : prev;
        }, []);

        this.splashesBs.next(splashes);
    }

    private setMeta(product: ProductDetailViewModel): void {
        if (!this.data) {
            return;
        }

        const flatProduct = flattenObjectProperties(product, false, 'text');
        const { metaTitle, metaDescription } = this.data;

        if (metaTitle) {
            const title = formatString(metaTitle, flatProduct, true);
            this.metaService.setTitle(title);
        }

        if (metaDescription) {
            const description = formatString(metaDescription, flatProduct, true);
            this.metaService.setDescription(description);
        }
    }

    private setOpenGraph(product: ProductDetailViewModel) {
        if (!this.data) {
            return;
        }

        const flatProduct = flattenObjectProperties(product, false, 'text');
        const { title, description, image, app_id: appId } = this.data;

        if (title) {
            const ogTitle = formatString(title, flatProduct, true);
            this.openGraphService.openGraphMeta('update', { type: 'og:title', value: ogTitle });
        }

        if (description) {
            const ogDescription = formatString(description, flatProduct, true);
            this.openGraphService.openGraphMeta('update', { type: 'og:description', value: ogDescription });
        }

        if (image || product.imageUrl) {
            this.openGraphService.openGraphMeta('update', { type: 'og:image', value: image?.src || product.imageUrl });
        }

        if (appId) {
            this.openGraphService.openGraphMeta('update', { type: 'fb:app_id', value: appId });
        }
    }
}
